import {
  getCalendarDateKey,
  isValidCalendarDate,
  parseCalendarDate,
  setCalendarDateYear,
} from "./calendar-date.helper";
import ProductSetting from "./product-setting.model";
import { ProductValue } from "../plushie/product.value";

export interface HolidayPeriodGroupDraft {
  key: string;
  productIds: string[];
  internationalDueDate?: Date;
  domesticDueDate?: Date;
}

function requiredDate(value?: Date): Date {
  if (!isValidCalendarDate(value)) {
    throw new Error("Every Holiday product group needs two valid dates.");
  }
  return value;
}

function getDatePairKey(group: HolidayPeriodGroupDraft): string | undefined {
  if (
    !isValidCalendarDate(group.internationalDueDate) ||
    !isValidCalendarDate(group.domesticDueDate)
  ) {
    return undefined;
  }

  return `${getCalendarDateKey(
    group.internationalDueDate
  )}:${getCalendarDateKey(group.domesticDueDate)}`;
}

export function combineMatchingGroups(
  groups: HolidayPeriodGroupDraft[]
): HolidayPeriodGroupDraft[] {
  const byDatePair = new Map<string, HolidayPeriodGroupDraft>();
  const result: HolidayPeriodGroupDraft[] = [];

  groups.forEach((group) => {
    const pair = getDatePairKey(group);
    const existing = pair ? byDatePair.get(pair) : undefined;
    if (!existing) {
      const copy = { ...group, productIds: group.productIds.slice() };
      result.push(copy);
      if (pair) {
        byDatePair.set(pair, copy);
      }
      return;
    }

    existing.productIds.push(...group.productIds);
  });

  return result;
}

export function groupProductSettings(
  settings: ProductSetting[],
  createKey: () => string
): HolidayPeriodGroupDraft[] {
  const groups = settings.map((setting) => ({
    key: createKey(),
    productIds: [setting.product],
    internationalDueDate: parseCalendarDate(setting.internationalDueDate),
    domesticDueDate: parseCalendarDate(setting.domesticDueDate),
  }));

  return combineMatchingGroups(groups);
}

export function copyProductSettingsToDraft(
  settings: ProductSetting[],
  createKey: () => string,
  year: number
): HolidayPeriodGroupDraft[] {
  return combineMatchingGroups(
    groupProductSettings(settings, createKey).map((group) => ({
      ...group,
      internationalDueDate: setCalendarDateYear(
        requiredDate(group.internationalDueDate),
        year
      ),
      domesticDueDate: setCalendarDateYear(
        requiredDate(group.domesticDueDate),
        year
      ),
    }))
  );
}

export function validateGroupDraft(
  groups: HolidayPeriodGroupDraft[]
): string[] {
  const errors: string[] = [];
  const assignedProducts = new Set<string>();

  groups.forEach((group) => {
    if (!group.productIds.length) {
      errors.push("Each Holiday product group needs at least one product.");
    }
    if (!isValidCalendarDate(group.internationalDueDate)) {
      errors.push(
        "Each Holiday product group needs an international due date."
      );
    }
    if (!isValidCalendarDate(group.domesticDueDate)) {
      errors.push("Each Holiday product group needs a domestic due date.");
    }
    group.productIds.forEach((productId) => {
      if (assignedProducts.has(productId)) {
        errors.push("A product can only belong to one Holiday product group.");
      }
      assignedProducts.add(productId);
    });
  });

  return Array.from(new Set(errors));
}

export function buildProductSettings(
  groups: HolidayPeriodGroupDraft[],
  periodId: string,
  existingSettings: ProductSetting[],
  createId: () => string
): ProductSetting[] {
  const errors = validateGroupDraft(groups);
  if (errors.length) {
    throw new Error(errors.join(" "));
  }

  const existingByProduct = new Map(
    existingSettings
      .filter((setting) => setting.period === periodId)
      .map((setting): [string, ProductSetting] => [setting.product, setting])
  );

  return combineMatchingGroups(groups).reduce(
    (settings: ProductSetting[], group) => {
      group.productIds.forEach((productId) => {
        const existing = existingByProduct.get(productId);
        const setting = new ProductSetting(
          existing ? existing.id : createId(),
          periodId,
          productId as ProductValue,
          parseCalendarDate(requiredDate(group.internationalDueDate)),
          parseCalendarDate(requiredDate(group.domesticDueDate))
        );
        setting.isNew = !existing;
        settings.push(setting);
      });
      return settings;
    },
    []
  );
}
